import React, { useEffect, useState,useContext } from 'react';
import { Table, Input, Button, Space, Modal, Form, InputNumber, Switch, message, Row, Col } from 'antd';
import { EditOutlined, SearchOutlined, FileExcelOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import api from '../../../api/axios';
import { AuthContext } from "../../../context/AuthContext";
const SparepartControl = () => {
  const [spareparts, setSpareparts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [retailForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const { user } = useContext(AuthContext);

const isSuperAdmin = user?.id === 1; // 👈 ONLY THIS USER
  const [retailModalVisible, setRetailModalVisible] = useState(false);
  const [editingRetailItem, setEditingRetailItem] = useState(null);
  // Fetch spareparts
  const fetchSpareparts = async () => {
    try {
      const res = await api.get('/api/spareparts');
      setSpareparts(res.data);
      setFiltered(res.data);
    } catch (error) {
      console.error(error);
      messageApi.error('Failed to fetch spareparts');
    }
  };

  useEffect(() => {
    fetchSpareparts();
  }, []);

  // Handle search
  const handleSearch = (value) => {
    const searchLower = value.toLowerCase();
    const filteredData = spareparts.filter(
      (item) =>
        item.partName?.toLowerCase().includes(searchLower) ||
        item.importerId?.toLowerCase().includes(searchLower) ||
        (item.price?.toString().includes(searchLower)) ||
        (item.retailPrice?.toString().includes(searchLower)) ||
        (item.isActive ? 'active' : 'inactive').includes(searchLower)
    );
    setFiltered(filteredData);
  };

  // Show edit price modal
  const showEditModal = (record) => {
    setEditingItem(record);
    form.setFieldsValue({ price: record.price });
    setIsModalVisible(true);
  };

  // Update price
  const handlePriceUpdate = async (values) => {
    try {
      const payload = { price: values.price };
      const res = await api.put(`/api/spareparts/${editingItem.id}`, payload);

      const updated = spareparts.map((sp) =>
        sp.id === editingItem.id ? { ...sp, price: res.data.sparepart?.price ?? values.price } : sp
      );
      setSpareparts(updated);
      setFiltered(updated);
      messageApi.success('Price updated successfully!');
      setIsModalVisible(false);
      form.resetFields();
      setEditingItem(null);
    } catch (error) {
      console.error(error);
      messageApi.error('Failed to update price');
    }
  };

  // Show edit retail price modal
  const showRetailModal = (record) => {
    setEditingRetailItem(record);
    retailForm.setFieldsValue({ retailPrice: record.retailPrice });
    setRetailModalVisible(true);
  };

  // Update retail price
  const handleRetailPriceUpdate = async (values) => {
    try {
      const payload = { retailPrice: values.retailPrice };
      const res = await api.put(`/api/spareparts/${editingRetailItem.id}/retail-price`, payload);

      const updated = spareparts.map((sp) =>
        sp.id === editingRetailItem.id ? { ...sp, retailPrice: res.data.sparepart?.retailPrice ?? values.retailPrice } : sp
      );
      setSpareparts(updated);
      setFiltered(updated);
      messageApi.success('Retail price updated successfully!');
      setRetailModalVisible(false);
      retailForm.resetFields();
      setEditingRetailItem(null);
    } catch (error) {
      console.error(error);
      messageApi.error('Failed to update retail price');
    }
  };

  // Toggle active/inactive
  const handleToggleActive = async (record) => {
    try {
      const payload = { isActive: !record.isActive };
      const res = await api.put(`/api/spareparts/${record.id}`, payload);
      const updated = spareparts.map((sp) =>
        sp.id === record.id ? { ...sp, isActive: res.data.isActive } : sp
      );
      setSpareparts(updated);
      setFiltered(updated);
      messageApi.success(
        `Sparepart ${res.data.isActive ? 'activated' : 'deactivated'} successfully!`
      );
    } catch (error) {
      console.error(error);
      messageApi.error('Failed to update status');
    }
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (!filtered.length) {
      messageApi.warning('No data to export');
      return;
    }

    const exportData = filtered.map((item) => ({
      'Importer ID': item.importerId,
      'Part Number': item.partNumber,
      'Part Name': item.partName,
      UOM: item.measureUnit,
      Price: item.price,
      'Retail Price': item.retailPrice,
      Status: item.isActive ? 'Active' : 'Inactive',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Spareparts');

    // Auto-size columns roughly
    const colWidths = Object.keys(exportData[0]).map((key) => ({
      wch: Math.max(
        key.length,
        ...exportData.map((row) => String(row[key] ?? '').length)
      ) + 2,
    }));
    worksheet['!cols'] = colWidths;

    const dateStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `spareparts_${dateStr}.xlsx`);
    messageApi.success('Exported to Excel successfully!');
  };

  const columns = [
    { title: 'ID', dataIndex: 'importerId', key: 'importerId' },
    { title: 'Part Number', dataIndex: 'partNumber', key: 'partNumber' },
    { title: 'Part Name', dataIndex: 'partName', key: 'partName' },
    { title: 'UOM', dataIndex: 'measureUnit', key: 'measureUnit' },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price, record) => (
        <Space>
          {price}
          <Button
            size="small"
            icon={<EditOutlined />}
            disabled={!isSuperAdmin}
            onClick={() => showEditModal(record)}
          />
        </Space>
      ),
    },
    {
      title: 'Retail Price',
      dataIndex: 'retailPrice',
      key: 'retailPrice',
      render: (retailPrice, record) => (
        <Space>
          {retailPrice ?? 0}
          <Button
            size="small"
            icon={<EditOutlined />}
            disabled={!isSuperAdmin}
            onClick={() => showRetailModal(record)}
          />
        </Space>
      ),
    },
  {
  title: 'Active',
  dataIndex: 'isActive',
  key: 'isActive',
  render: (isActive, record) => (
    <Switch
      checked={isActive}
      disabled={!isSuperAdmin}
      onChange={() => handleToggleActive(record)}
    />
  ),
},
  ];

  return (
    <div>
      {contextHolder}

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }} justify="space-between">
        <Col xs={24} sm={12} md={8}>
          <Input.Search
            placeholder="Search by name, importer, price, status"
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={handleSearch}
            onChange={(e) => setSearchValue(e.target.value)}
            value={searchValue}
          />
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={handleExportExcel}
            style={{ backgroundColor: '#217346', borderColor: '#217346' }}
          >
            Export to Excel
          </Button>
        </Col>
      </Row>

      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 6 }}
        bordered
        scroll={{ x: 'max-content' }}
      />

      <Modal
        title="Update Price"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        centered
      >
        <Form form={form} layout="vertical" onFinish={handlePriceUpdate}>
          <Form.Item
            name="price"
            label="Price"
            rules={[{ required: true, message: 'Please enter the new price' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Update Retail Price"
        open={retailModalVisible}
        onCancel={() => setRetailModalVisible(false)}
        onOk={() => retailForm.submit()}
        centered
      >
        <Form form={retailForm} layout="vertical" onFinish={handleRetailPriceUpdate}>
          <Form.Item
            name="retailPrice"
            label="Retail Price"
            rules={[{ required: true, message: 'Please enter the new retail price' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SparepartControl;